<script lang="ts">
  import '@/js/materialPropertiesModal'
  import '@/map/new/multiselect.js'
  import '@/js/tokenfield.js'
  import '@/js/autocomplete.js'
  import '@/frontend/jquery-3.3.1.min.js'
  import {onMount, tick} from 'svelte'
  import {SvelteMap} from 'svelte/reactivity'
  import {t} from '$i18n/i18n'
  import {currentLot} from '../utils/fakStores'
  import {updateMaterialPropertyComments, validateAndSaveLot} from '../../types/lot'
  import mmLogoAnimation from '@/animations/MmLogo.gif'
  import type {MaterialProperty} from '$lib/mmpv/types/materialProperty'
  import Button from '$lib/atoms/Button.svelte'
  import IconCheckmark from '$svg/icon-checkmark.svg?component'
  import type {Material} from '$lib/map/types/material'

  let mPropertiesModal: MaterialPropertiesModal | null = null
	let currentMaterial: Material | null = null

	const draftPropertiesByMaterialId = new SvelteMap<string, string>()
	let readonly = $state(false)
	let dialogMode = $state(false)
	let containerEl: HTMLElement | undefined
	let inputField: HTMLInputElement

	function normalizeMaterialPropertiesForModal(materialProperties?: string): string {
		if (!materialProperties) return ''
		try {
			const parsed = JSON.parse(materialProperties)
			if (Array.isArray(parsed)) {
				return JSON.stringify(parsed)
			}
			if (parsed?.data && Array.isArray(parsed.data)) {
				return JSON.stringify(parsed.data)
			}
		} catch {
			// ignore and fallback to empty seed
		}
		return ''
	}

	export async function init() {
		dialogMode = false
		await tick()
		if (!$currentLot || !$currentLot.material) {
			console.warn('Cannot initialize material properties: currentLot or material is null')
			return
		}
		try {
			let url = $currentLot?.id ? `/api/lot/${$currentLot?.id}` : '/api/lot/new'
			readonly = $currentLot ? !$currentLot?.lotViewFieldsChangeMap.canEditLot : false
			let materialId = $currentLot?.material?.id
			let materialText = $currentLot?.material?.text
			inputField.value = $currentLot?.materialProperties || ''
			// Needs to be loaded as a script and not as an ES module, since Sirius does not like that...
			mPropertiesModal = new window.MaterialPropertiesModal(
				url,
				readonly,
				materialId,
				materialText,
				true,
				containerEl
			)
			await mPropertiesModal!.checkAndReloadDependencies()
			mPropertiesModal!.resetContent()
			await mPropertiesModal!.init()
		} catch (e) {
			console.warn('Could not initialize properties modal. Trying again in 5s...', e)
			setTimeout(init, 5000)
		}
	}

	export async function initWithoutLot(material: Material, materialProperties?: string) {
		mPropertiesModal?.resetContent()
		currentMaterial = material
		const materialKey = String(material.id)
		const cachedMaterialProperties = draftPropertiesByMaterialId.get(materialKey)
		const incomingMaterialProperties = normalizeMaterialPropertiesForModal(materialProperties)
		inputField.value =
			(incomingMaterialProperties ? incomingMaterialProperties : cachedMaterialProperties) ?? ''
		dialogMode = true
		mPropertiesModal = await window.openPropertiesModalNoQuery(
			'/api/propertiesStructure',
			false,
			material.id,
			material.text,
			false,
			null,
			containerEl
		)
	}

	async function onMaterialPropertiesSave(
		e: CustomEvent<{ materialId: string; data: MaterialProperty[] }>
	) {
		const serializedProperties = JSON.stringify(e.detail)
		const serializedModalProperties = JSON.stringify(e.detail.data)
		inputField.value = serializedModalProperties
		const materialKey = String(currentMaterial?.id ?? e.detail.materialId)
		draftPropertiesByMaterialId.set(materialKey, serializedModalProperties)
		if (
			$currentLot?.lotViewFieldsChangeMap.canEditLot &&
			$currentLot?.material?.id === e.detail.materialId
		) {
			$currentLot.materialProperties = serializedProperties
			await updateMaterialPropertyComments()
			if ($currentLot.id !== 'new') {
				await validateAndSaveLot()
			}
		}
		close()
	}

	function close() {
		dispatchEvent(new CustomEvent('closeMaterialProperties'))
	}

	onMount(() => {
		const handler = (e: Event) =>
			onMaterialPropertiesSave(e as CustomEvent<{ materialId: string; data: MaterialProperty[] }>)

		window.addEventListener('materialPropertiesSave', handler)

		return () => {
			window.removeEventListener('materialPropertiesSave', handler)
		}
	})
</script>

<div
	bind:this={containerEl}
	class={{
		'material-properties-modal-container': true,
		'material-properties-modal-container--offscreen': !dialogMode
	}}
	id="materialPropertiesModal"
>
	<!-- For some reason whatsoever, we have to include the translateY to see the modal -->
	<div class="mp-container">
		<div class="mpm--header">
			<!-- svelte-ignore a11y_missing_content -->
			<!-- Is filled by materialPropertiesModal.js -->
			<h2 id="mp-header-text" class="modal-title"></h2>
			<span
			>Definiere und bearbeite hier die Materialeigenschaften des ausgewählten Materials anhand
				folgender Felder.</span
			>
		</div>
		<img id="mp-loading-animation" alt="LoadingAnimation" src={mmLogoAnimation} />

		<div id="mp-content" class="container"></div>

		<div class="mpm--buttons-container">
			<Button variant="secondary" onclick={close}>{$t('NLS.cancel')}</Button>
			<Button
				variant="primary"
				disabled={readonly}
				onclick={() => mPropertiesModal?.checkPropsAndDispatchEvent()}
			>
				{$t('UI.button.done')}
				<IconCheckmark height="20" width="20" />
			</Button>
		</div>
		<!-- Must live inside containerEl: MaterialPropertiesModal reads #material-properties-input via this.#root -->
		<input
			bind:this={inputField}
			id="material-properties-input"
			type="hidden"
			name="materialProperties"
		/>
	</div>
</div>

<style>
	.material-properties-modal-container {
		overflow: scroll;
		-ms-overflow-style: none; /* IE and Edge */
		scrollbar-width: none; /* Firefox */

		&::-webkit-scrollbar {
			display: none;
		}
	}

	.material-properties-modal-container--offscreen {
		transform: translateY(-100%);
	}

	/* Chrome, Safari, Opera */
	.material-properties-modal-container::-webkit-scrollbar {
		display: none;
	}

	@media (width >= 1200px) {
		.material-properties-modal-container {
			width: 1170px;
		}
	}

	:global(#mp-content *) {
		font-family: Roboto, sans-serif !important;
	}

	:global(#mp-content textarea),
	:global(#mp-content select),
	:global(#mp-content input.token-input) {
		font-size: 0.75rem;
	}

	/* the default value of the select which is not a placeholder but a disabled value should have the same color as the other inputs */
	:global(#mp-content select.form-control:has(option:checked[value=''])) {
		color: rgb(117, 117, 117);
	}
	:global(#mp-content select.form-control option:not([disabled])) {
		color: black;
	}

	.mpm--buttons-container {
		display: flex;
		justify-content: flex-end;
		gap: 8px;
		padding: 16px;
	}

	.mpm--header {
		font-family: Roboto, sans-serif;
		font-style: normal;
		font-weight: 400;
		font-size: 0.75rem;
		line-height: normal;
		color: #000;
		padding: 16px;
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: 4px;
	}

	/* Multiselect input/selected items area */
	:global(.form-control) {
		display: flex !important; /* Needed to overwrite bootstrap style... */
		flex-wrap: wrap;
		gap: 8px;

		&.focus {
			border: 1px solid #58c079; /* --Greens-Old-Primary-Green */
		}

		&.disabled {
			background-color: #e9ecef;
		}
	}

	/* Multiselect dropdown */
	:global(.autocomplete-wrapper) {
		position: absolute;
		display: none;
		z-index: 999;
		background: #fff;
		border-right: 1px solid #d9d9d9; /* --grays-old-primary-gray-old */
		border-bottom: 1px solid #d9d9d9; /* --grays-old-primary-gray-old */
		border-left: 1px solid #d9d9d9; /* --grays-old-primary-gray-old */
		scrollbar-width: none; /* Firefox */
		-ms-overflow-style: none; /* Internet Explorer 10+ */

		&::-webkit-scrollbar {
			/* WebKit */
			display: none;
		}
	}

	/* Multiselect dropdown item */
	:global(.autocomplete-row) {
		padding: 8px 12px;
		color: #000;
		font-family: Roboto, sans-serif;
		font-size: 12px;
		font-style: normal;
		font-weight: 400;
		line-height: 10px;

		&:hover {
			background: var(--greens-old-light-green-iv-old, #acdfbc);
			cursor: pointer;
		}
	}

	/* Multiselect input field */
	:global(.token-input) {
		border: none;
		cursor: pointer;

		&:focus {
			outline: none;
		}
	}

	/* When the multiselect is disabled, also tint the token input area */
	:global(.form-control.disabled .token-input) {
		background-color: #e9ecef;
	}

	/* Multiselect selected item */
	:global(.token) {
		width: fit-content;
		max-width: 100%;
		display: flex;
		flex-direction: row;
		height: 21px;
		padding: 2px 6px;
		justify-content: center;
		align-items: center;
		gap: 10px;
		border-radius: 19px;
		border: 1px solid #58c079; /* --Greens-Old-Primary-Green */
		background: #fff;

		/* Selected item label */
		& > span.token-label {
			color: #000;
			font-family: Roboto, sans-serif;
			font-size: 12px;
			font-style: normal;
			font-weight: 400;
			line-height: 10px;
			overflow: hidden;
			white-space: nowrap;
			text-overflow: ellipsis;
		}

		/* Remove item close x (wondergem just puts a small x character here, so we have to tweek it a little) */
		& > a.close {
			color: #9e9e9e; /* Grays Old/Gray III (Old) */
			text-decoration: none;
			font-size: 18px;
			margin-top: -1px;
		}
	}

	/* Icon for tippy tooltip element. */
	:global(.info-icon) {
		display: block;
		content: '';
		background: url('/assets/images/InfoIcon20px.svg') no-repeat center;
		background-size: 20px 20px;
		height: 20px;
		width: 20px;
	}
</style>
